import React, { useState, useEffect } from "react";
import { X, Star, Save } from "lucide-react";
import { toast } from "react-toastify";
import API_URL from "../../config";

const EditReviewModal = ({ isOpen, onClose, review, onUpdate }) => {
    const [formData, setFormData] = useState({
        name: "",
        destination: "",
        rating: 1,
        comment: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (review) {
            setFormData({
                name: review.name || "",
                destination: review.destination || "",
                rating: review.rating || 1,
                comment: review.comment || ""
            });
        }
    }, [review]);

    if (!isOpen || !review) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const response = await fetch(`${API_URL}/reviews/${review._id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                toast.success("Review updated successfully");
                onUpdate();
                onClose();
            } else {
                const data = await response.json();
                toast.error(data.message || "Failed to update review");
            }
        } catch (error) {
            console.error("Error updating review:", error);
            toast.error("Error updating review");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-sky-900/40 backdrop-blur-sm" onClick={onClose}></div>
            <div className="relative bg-white rounded-3xl shadow-2xl border border-sky-100 w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-300">
                <div className="p-1">
                    <div className="bg-sky-50/50 rounded-2xl p-8">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-black text-sky-950">Edit Review</h3>
                            <button onClick={onClose} className="p-2 hover:bg-sky-100 rounded-xl transition text-sky-400 hover:text-sky-600">
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="text-xs font-black text-sky-900/40 uppercase tracking-widest ml-1">Reviewer Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-5 py-3.5 bg-white border border-sky-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950 mt-1"
                                    required
                                />
                            </div>

                            <div>
                                <label className="text-xs font-black text-sky-900/40 uppercase tracking-widest ml-1">Destination</label>
                                <input
                                    type="text"
                                    value={formData.destination}
                                    onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                                    className="w-full px-5 py-3.5 bg-white border border-sky-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950 mt-1"
                                    required
                                />
                            </div>

                            <div>
                                <label className="text-xs font-black text-sky-900/40 uppercase tracking-widest ml-1">Rating</label>
                                <div className="flex gap-2 mt-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, rating: star })}
                                            className="transition-transform active:scale-90"
                                        >
                                            <Star
                                                size={28}
                                                className={star <= formData.rating ? "fill-amber-400 text-amber-400" : "text-gray-200"}
                                            />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-black text-sky-900/40 uppercase tracking-widest ml-1">Comment</label>
                                <textarea
                                    value={formData.comment}
                                    onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                                    rows="4"
                                    className="w-full px-5 py-3.5 bg-white border border-sky-100 rounded-2xl focus:outline-none focus:ring-4 focus:ring-sky-500/10 focus:border-sky-400 transition-all font-bold text-sky-950 mt-1 resize-none"
                                    required
                                />
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 py-4 px-6 bg-white border border-sky-100 text-sky-600 font-black rounded-2xl hover:bg-sky-50 transition shadow-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="flex-1 py-4 px-6 bg-sky-500 text-white font-black rounded-2xl shadow-xl shadow-sky-500/30 hover:bg-sky-600 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
                                >
                                    {isSubmitting ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <Save size={20} />
                                            Update Review
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditReviewModal;
